import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Plus, Edit, Trash2, Star } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Modal from '../ui/Modal';
import ConfirmDialog from '../modals/ConfirmDialog';
import apiService from '../../services/api';
import { ClientLocationDto } from '../../types';
import PhoneInput from '../forms/PhoneInput';
import StateSelect from '../forms/StateSelect';

interface Props {
  clientId: number;
}

const locationSchema = yup.object({
  locationName: yup.string().required('Location name is required').max(150, 'Location name must be less than 150 characters'),
  locationType: yup.string().required('Location type is required'),
  address: yup.string().required('Address is required').max(255, 'Address must be less than 255 characters'),
  city: yup.string().required('City is required').max(100, 'City must be less than 100 characters'),
  state: yup.string().required('State is required').max(50, 'State must be less than 50 characters'),
  zipCode: yup.string().required('Zip code is required').max(20, 'Zip code must be less than 20 characters'),
  contactName: yup.string().max(150, 'Contact name must be less than 150 characters').nullable(),
  contactPhone: yup.string().max(20, 'Contact phone must be less than 20 characters').nullable(),
  contactEmail: yup.string().email('Please enter a valid email address').max(256, 'Email must be less than 256 characters').nullable(),
  specialInstructions: yup.string().max(500, 'Special instructions must be less than 500 characters').nullable(),
  taxRate: yup.number().min(0, 'Tax rate must be 0 or greater').max(1, 'Tax rate must be 1 or less').nullable(),
  isPrimary: yup.boolean(),
  isActive: yup.boolean(),
  canReceiveWorkOrders: yup.boolean()
});

type LocationFormData = yup.InferType<typeof locationSchema>;

const emptyForm: Partial<ClientLocationDto> = {
  locationName: '',
  locationType: 'Store', // Use string instead of number
  address: '',
  city: '',
  state: '',
  zipCode: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  isPrimary: false,
  isActive: true,
  canReceiveWorkOrders: true,
  specialInstructions: '',
  taxRate: null
};

const ClientLocationsPanel: React.FC<Props> = ({ clientId }) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<ClientLocationDto | null>(null);
  const [taxRateDisplay, setTaxRateDisplay] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<ClientLocationDto | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch
  } = useForm<LocationFormData>({
    resolver: yupResolver(locationSchema),
    defaultValues: {
      locationName: '',
      locationType: 'Store',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      specialInstructions: '',
      taxRate: null,
      isPrimary: false,
      isActive: true,
      canReceiveWorkOrders: true
    }
  });

  // Helper function to convert locationType string to display name
  const getLocationTypeName = (type: string): string => {
    switch (type) {
      case 'Headquarters': return 'Headquarters';
      case 'Store': return 'Store';
      case 'Warehouse': return 'Warehouse';
      case 'Distribution': return 'Distribution';
      case 'Other': return 'Other';
      default: return 'Unknown';
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['client-locations', clientId],
    queryFn: () => apiService.getClientLocations(clientId)
  });
  const locations: ClientLocationDto[] = data?.data || [];

  // Sort locations: primary first, then alphabetically by name
  const sortedLocations = [...locations].sort((a, b) => {
    // Primary locations first
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    
    // Then sort alphabetically by name
    return a.locationName.localeCompare(b.locationName);
  });

  const createMutation = useMutation({
    mutationFn: (payload: Partial<ClientLocationDto>) => {
      console.log('ClientLocationsPanel: createMutation.mutationFn called with:', payload);
      
      // Transform the data to match CreateClientLocationDto
      const transformedData = {
        locationName: payload.locationName || '',
        locationType: payload.locationType || 'Store', // Use string directly
        address: payload.address,
        city: payload.city,
        state: payload.state,
        zipCode: payload.zipCode,
        contactName: payload.contactName,
        contactPhone: payload.contactPhone,
        contactEmail: payload.contactEmail,
        isPrimary: payload.isPrimary || false,
        isActive: payload.isActive !== undefined ? payload.isActive : true,
        canReceiveWorkOrders: payload.canReceiveWorkOrders !== undefined ? payload.canReceiveWorkOrders : true,
        specialInstructions: payload.specialInstructions,
        taxRate: payload.taxRate
      };
      
      console.log('ClientLocationsPanel: transformed create data:', transformedData);
      return apiService.createClientLocation(clientId, transformedData);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['client-locations', clientId] });
      setIsModalOpen(false);
      toast.success(`Location "${response.data?.locationName || 'Unknown'}" created successfully!`);
    },
    onError: (error: any) => {
      console.error('Error creating location:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.errors?.join(', ') || 
                          error.message || 
                          'Unknown error';
      toast.error(`Error creating location: ${errorMessage}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: number; data: Partial<ClientLocationDto> }) => {
      console.log('ClientLocationsPanel: updateMutation.mutationFn called with:', payload);
      
      // Transform the data to match UpdateClientLocationDto
      const transformedData = {
        locationName: payload.data.locationName || '',
        locationType: payload.data.locationType || 'Store', // Use string directly
        address: payload.data.address,
        city: payload.data.city,
        state: payload.data.state,
        zipCode: payload.data.zipCode,
        contactName: payload.data.contactName,
        contactPhone: payload.data.contactPhone,
        contactEmail: payload.data.contactEmail,
        isPrimary: payload.data.isPrimary || false,
        isActive: payload.data.isActive !== undefined ? payload.data.isActive : true,
        canReceiveWorkOrders: payload.data.canReceiveWorkOrders !== undefined ? payload.data.canReceiveWorkOrders : true,
        specialInstructions: payload.data.specialInstructions,
        taxRate: payload.data.taxRate
      };
      
      console.log('ClientLocationsPanel: transformed data:', transformedData);
      console.log('ClientLocationsPanel: calling apiService.updateClientLocation with:', { clientId, locationId: payload.id, data: transformedData });
      return apiService.updateClientLocation(clientId, payload.id, transformedData);
    },
    onSuccess: (response) => {
      console.log('ClientLocationsPanel: updateMutation.onSuccess called with:', response);
      queryClient.invalidateQueries({ queryKey: ['client-locations', clientId] });
      setIsModalOpen(false);
      setEditing(null);
      toast.success(`Location "${response.data?.locationName || 'Unknown'}" updated successfully!`);
    },
    onError: (error: any) => {
      console.error('ClientLocationsPanel: updateMutation.onError called with:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.errors?.join(', ') || 
                          error.message || 
                          'Unknown error';
      toast.error(`Error updating location: ${errorMessage}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteClientLocation(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-locations', clientId] });
      toast.success('Location deleted successfully!');
    },
    onError: (error: any) => {
      console.error('Error deleting location:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.errors?.join(', ') || 
                          error.message || 
                          'Unknown error';
      toast.error(`Error deleting location: ${errorMessage}`);
    }
  });

  const handleDelete = (location: ClientLocationDto) => {
    // Validation rules
    if (sortedLocations.length === 1) {
      toast.error('Cannot delete the only location. Every client must have at least one location.');
      return;
    }

    setLocationToDelete(location);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (locationToDelete) {
      deleteMutation.mutate(locationToDelete.id);
      setShowDeleteConfirm(false);
      setLocationToDelete(null);
    }
  };

  const setPrimaryMutation = useMutation({
    mutationFn: (id: number) => apiService.setPrimaryClientLocation(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-locations', clientId] });
      toast.success('Primary location updated successfully!');
    },
    onError: (error: any) => {
      console.error('Error setting primary location:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.errors?.join(', ') || 
                          error.message || 
                          'Unknown error';
      toast.error(`Error setting primary location: ${errorMessage}`);
    }
  });

  const openAdd = () => {
    setEditing(null);
    reset({
      locationName: '',
      locationType: 'Store',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      specialInstructions: '',
      taxRate: null,
      isPrimary: false,
      isActive: true,
      canReceiveWorkOrders: true
    });
    setTaxRateDisplay('');
    setIsModalOpen(true);
  };

  const openEdit = (loc: ClientLocationDto) => {
    setEditing(loc);
    const taxRateValue = loc.taxRate !== null && loc.taxRate !== undefined ? loc.taxRate : null;
    reset({
      locationName: loc.locationName || '',
      locationType: loc.locationType || 'Store',
      address: loc.address || '',
      city: loc.city || '',
      state: loc.state || '',
      zipCode: loc.zipCode || '',
      contactName: loc.contactName || '',
      contactPhone: loc.contactPhone || '',
      contactEmail: loc.contactEmail || '',
      specialInstructions: loc.specialInstructions || '',
      taxRate: taxRateValue,
      isPrimary: loc.isPrimary || false,
      isActive: loc.isActive !== undefined ? loc.isActive : true,
      canReceiveWorkOrders: loc.canReceiveWorkOrders !== undefined ? loc.canReceiveWorkOrders : true
    });
    setTaxRateDisplay(taxRateValue !== null ? (taxRateValue * 100).toFixed(2) : '');
    setIsModalOpen(true);
  };

  // Watch taxRate to sync taxRateDisplay
  const watchedTaxRate = watch('taxRate');
  useEffect(() => {
    if (watchedTaxRate !== null && watchedTaxRate !== undefined) {
      setTaxRateDisplay((watchedTaxRate * 100).toFixed(2));
    } else {
      setTaxRateDisplay('');
    }
  }, [watchedTaxRate]);

  const onSubmit = (data: LocationFormData) => {
    console.log('ClientLocationsPanel: onSubmit called with data:', data);
    console.log('ClientLocationsPanel: editing:', editing);
    
    // Validation: Check for duplicate location names
    const existingLocation = sortedLocations.find(loc => 
      loc.locationName.toLowerCase() === data.locationName?.toLowerCase() && 
      loc.id !== editing?.id
    );
    
    if (existingLocation) {
      toast.error('A location with this name already exists for this client. Please choose a different name.');
      return;
    }
    
    // Transform data to match API expectations
    const formData: Partial<ClientLocationDto> = {
      locationName: data.locationName,
      locationType: data.locationType,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      contactName: data.contactName || undefined,
      contactPhone: data.contactPhone || undefined,
      contactEmail: data.contactEmail || undefined,
      specialInstructions: data.specialInstructions || undefined,
      taxRate: data.taxRate || null,
      isPrimary: data.isPrimary || false,
      isActive: data.isActive !== undefined ? data.isActive : true,
      canReceiveWorkOrders: data.canReceiveWorkOrders !== undefined ? data.canReceiveWorkOrders : true
    };
    
    if (editing) {
      console.log('ClientLocationsPanel: Calling updateMutation.mutate');
      updateMutation.mutate({ id: editing.id, data: formData });
    } else {
      console.log('ClientLocationsPanel: Calling createMutation.mutate');
      createMutation.mutate(formData);
    }
  };

  return (
    <div className="mt-0">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700">Locations</h4>
        <button onClick={openAdd} className="px-3 py-2 text-sm bg-accent-color text-white rounded-md hover:bg-accent-color-600 flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>Add Location</span>
        </button>
      </div>

      <div className="border border-gray-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-50 text-xs font-medium text-gray-600 px-3 py-2">
          <div className="col-span-2">Name</div>
          <div className="col-span-2">Address</div>
          <div>Type</div>
          <div>Primary</div>
          <div>Active</div>
        </div>
        {isLoading ? (
          <div className="p-3 text-sm text-gray-500">Loading locations...</div>
        ) : sortedLocations.length === 0 ? (
          <div className="p-3 text-sm text-gray-500">No locations yet.</div>
        ) : (
          sortedLocations.map((loc) => (
            <div 
              key={loc.id} 
              className="group relative grid grid-cols-7 items-center px-3 py-2 border-t text-sm hover:bg-gray-50"
            >
              <div className="col-span-2 font-medium text-gray-900">{loc.locationName}</div>
              <div className="col-span-2 text-gray-600">{loc.address || '-'}{loc.city ? `, ${loc.city}` : ''}{loc.state ? `, ${loc.state}` : ''}</div>
              <div>{getLocationTypeName(loc.locationType)}</div>
              <div>
                {loc.isPrimary ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800"><Star className="h-3 w-3 mr-1"/>Primary</span>
                ) : (
                  <button onClick={() => setPrimaryMutation.mutate(loc.id)} className="text-xs text-accent-color hover:text-accent-color-800">Set Primary</button>
                )}
              </div>
              <div className="relative">
                <div className="group-hover:opacity-0 transition-opacity duration-200">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${loc.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{loc.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
                  <button 
                    onClick={() => openEdit(loc)} 
                    className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                    title="Edit location"
                  >
                    <Edit className="h-4 w-4"/>
                  </button>
                  <button 
                    onClick={() => handleDelete(loc)} 
                    className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
                    title="Delete location"
                  >
                    <Trash2 className="h-4 w-4"/>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditing(null); }}
        title={editing ? 'Edit Location' : 'Add Location'}
        maxWidth="2xl"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              {/* Checkboxes at the top */}
              <div className="md:col-span-2 flex items-center space-x-6 pb-4 border-b border-gray-200">
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center space-x-2">
                      <input 
                        id="isActive" 
                        type="checkbox" 
                        checked={field.value} 
                        onChange={e => field.onChange(e.target.checked)} 
                      />
                      <label htmlFor="isActive" className="text-sm text-gray-700">Active</label>
                    </div>
                  )}
                />
                <Controller
                  name="canReceiveWorkOrders"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center space-x-2">
                      <input 
                        id="canReceive" 
                        type="checkbox" 
                        checked={field.value} 
                        onChange={e => field.onChange(e.target.checked)} 
                      />
                      <label htmlFor="canReceive" className="text-sm text-gray-700">Can receive work orders</label>
                    </div>
                  )}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">Location Name <span className="text-red-500">*</span></label>
                <input 
                  {...register('locationName')}
                  className={`w-full border rounded px-3 py-2 ${errors.locationName ? 'border-red-300' : ''}`}
                />
                {errors.locationName && (
                  <p className="mt-1 text-sm text-red-600">{errors.locationName.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Type</label>
                <select 
                  {...register('locationType')}
                  className={`w-full border rounded px-3 py-2 ${errors.locationType ? 'border-red-300' : ''}`}
                >
                  <option value="Headquarters">Headquarters</option>
                  <option value="Store">Store</option>
                  <option value="Warehouse">Warehouse</option>
                  <option value="Distribution">Distribution</option>
                  <option value="Other">Other</option>
                </select>
                {errors.locationType && (
                  <p className="mt-1 text-sm text-red-600">{errors.locationType.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Contact Name</label>
                <input 
                  {...register('contactName')}
                  className={`w-full border rounded px-3 py-2 ${errors.contactName ? 'border-red-300' : ''}`}
                />
                {errors.contactName && (
                  <p className="mt-1 text-sm text-red-600">{errors.contactName.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Contact Phone</label>
                <Controller
                  name="contactPhone"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput 
                      value={field.value || ''} 
                      onChange={field.onChange} 
                      className={`w-full border rounded px-3 py-2 ${errors.contactPhone ? 'border-red-300' : ''}`}
                    />
                  )}
                />
                {errors.contactPhone && (
                  <p className="mt-1 text-sm text-red-600">{errors.contactPhone.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Contact Email</label>
                <input 
                  {...register('contactEmail')}
                  type="email"
                  className={`w-full border rounded px-3 py-2 ${errors.contactEmail ? 'border-red-300' : ''}`}
                />
                {errors.contactEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.contactEmail.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Tax Rate (%)</label>
                <Controller
                  name="taxRate"
                  control={control}
                  render={({ field }) => (
                    <>
                      <input
                        type="text"
                        placeholder="7.25 (for 7.25%)"
                        value={taxRateDisplay}
                        onChange={e => {
                          const value = e.target.value;
                          
                          // Allow empty
                          if (value === '') {
                            setTaxRateDisplay('');
                            field.onChange(null);
                            return;
                          }
                          
                          // Simple regex: allow numbers with optional decimal and up to 2 decimal places
                          if (!/^\d*\.?\d{0,2}$/.test(value)) {
                            return;
                          }
                          
                          setTaxRateDisplay(value);
                          
                          const num = parseFloat(value);
                          if (!isNaN(num) && num >= 0 && num <= 100) {
                            field.onChange(num / 100);
                          }
                        }}
                        className={`w-full border rounded px-3 py-2 ${errors.taxRate ? 'border-red-300' : ''}`}
                      />
                      <p className="text-xs text-gray-500 mt-1">Sales tax rate for this location (leave blank to use company default)</p>
                    </>
                  )}
                />
                {errors.taxRate && (
                  <p className="mt-1 text-sm text-red-600">{errors.taxRate.message}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-700 mb-1">Address <span className="text-red-500">*</span></label>
                <input 
                  {...register('address')}
                  className={`w-full border rounded px-3 py-2 ${errors.address ? 'border-red-300' : ''}`}
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">City <span className="text-red-500">*</span></label>
                <input 
                  {...register('city')}
                  className={`w-full border rounded px-3 py-2 ${errors.city ? 'border-red-300' : ''}`}
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">State <span className="text-red-500">*</span></label>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <StateSelect 
                      value={field.value || ''} 
                      onChange={field.onChange} 
                      placeholder="Select state"
                      error={errors.state?.message}
                    />
                  )}
                />
                {errors.state && (
                  <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Zip <span className="text-red-500">*</span></label>
                <input 
                  {...register('zipCode')}
                  className={`w-full border rounded px-3 py-2 ${errors.zipCode ? 'border-red-300' : ''}`}
                />
                {errors.zipCode && (
                  <p className="mt-1 text-sm text-red-600">{errors.zipCode.message}</p>
                )}
              </div>
              <div className="md:col-span-2 flex justify-end space-x-2 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => { setIsModalOpen(false); setEditing(null); }} className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="px-3 py-2 text-sm bg-accent-color text-white rounded-md hover:bg-accent-color-600 disabled:opacity-50">
                  {editing ? 'Update Location' : 'Create Location'}
                </button>
              </div>
        </form>
      </Modal>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setLocationToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Location"
        message={`Are you sure you want to delete "${locationToDelete?.locationName}"?${locationToDelete?.isPrimary ? ' This is the primary location. Deleting it will make the first remaining location the new primary.' : ''} This action cannot be undone.`}
        confirmText="Delete Location"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
        variant="danger"
      />
    </div>
  );
};

export default ClientLocationsPanel;


